import { useState, useMemo, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { AnimatePresence, motion } from 'framer-motion';
import {
  Globe, ShoppingCart, Smartphone, Layers, Database, Sparkles, Store, Building2,
  Feather, Rocket, Gem, Crown, FileText, Files, Grid3x3, Infinity as InfinityIcon,
  Lock, LayoutDashboard, CreditCard, CalendarCheck, UserCircle2, Plug, MessageSquare,
  Bot, Bell, BarChart3, FileEdit, Search, Palette, Award, Radio, TrendingUp, Users,
  Timer, CalendarRange, Clock, ShieldOff, ShieldCheck, ShieldHalf, ArrowRight, ArrowLeft,
  Check, Sparkle, Loader2, Gauge,
} from 'lucide-react';

/* ────────────────────────────────────────────────────────────────────────
   CONFIG — every number the consultant runs on lives here. Nothing below
   this block is hardcoded; tune the business logic by editing these maps.
   ──────────────────────────────────────────────────────────────────────── */

const SERVICE_BASE = {
  website:     { label: 'Website',           range: [12000, 20000],  icon: Globe },
  ecommerce:   { label: 'Ecommerce Store',   range: [25000, 70000],  icon: ShoppingCart },
  app:         { label: 'Mobile App',        range: [45000, 100000], icon: Smartphone },
  saas:        { label: 'SaaS Platform',     range: [80000, 200000], icon: Layers },
  crm:         { label: 'Custom CRM',        range: [80000, 200000], icon: Database },
  ai:          { label: 'AI Tool',           range: [35000, 200000], icon: Sparkles },
  marketplace: { label: 'Marketplace',       range: [70000, 180000], icon: Store },
  enterprise:  { label: 'Enterprise Software', range: [150000, 400000], icon: Building2 },
};

const COMPLEXITY = {
  basic:      { label: 'Basic',      mult: 1,    score: 30, icon: Feather },
  standard:   { label: 'Standard',   mult: 1.3,  score: 55, icon: Rocket },
  advanced:   { label: 'Advanced',   mult: 1.7,  score: 78, icon: Gem },
  enterprise: { label: 'Enterprise', mult: 2.3,  score: 100, icon: Crown },
};

const PAGES = {
  landing:  { label: 'Landing Page', add: 0,     icon: FileText },
  '5-10':   { label: '5–10 Pages',   add: 5000,  icon: Files },
  '10-20':  { label: '10–20 Pages',  add: 15000, icon: Grid3x3 },
  '20+':    { label: '20+ Pages',    add: 30000, icon: Grid3x3 },
  unlimited:{ label: 'Unlimited',    add: 50000, icon: InfinityIcon },
};

const FEATURES = {
  auth:          { label: 'Authentication',  cost: 5000,  icon: Lock,           backend: true },
  dashboard:     { label: 'Admin Dashboard', cost: 12000, icon: LayoutDashboard, backend: true },
  payment:       { label: 'Payment Gateway', cost: 15000, icon: CreditCard,     backend: true },
  booking:       { label: 'Booking System',  cost: 18000, icon: CalendarCheck,  backend: true },
  profiles:      { label: 'User Profiles',   cost: 8000,  icon: UserCircle2,    backend: false },
  api:           { label: 'API Integration', cost: 10000, icon: Plug,           backend: true },
  chat:          { label: 'Chat',            cost: 12000, icon: MessageSquare,  backend: true },
  ai:            { label: 'AI Integration',  cost: 25000, icon: Bot,            backend: true },
  notifications: { label: 'Notifications',   cost: 6000,  icon: Bell,           backend: false },
  analytics:     { label: 'Analytics',       cost: 8000,  icon: BarChart3,      backend: false },
  cms:           { label: 'CMS',             cost: 10000, icon: FileEdit,       backend: false },
  search:        { label: 'Search',          cost: 7000,  icon: Search,         backend: false },
};

const DESIGN = {
  template:     { label: 'Template',            mult: 0.9,  icon: Palette },
  professional: { label: 'Professional',        mult: 1,    icon: Palette },
  premium:      { label: 'Premium',              mult: 1.25, icon: Award },
  luxury:       { label: 'Luxury Custom Design', mult: 1.6,  icon: Gem },
};

const TRAFFIC = {
  '1k':   { label: '1K / mo',   icon: Radio,      infraWeight: 0 },
  '10k':  { label: '10K / mo',  icon: TrendingUp,  infraWeight: 1 },
  '100k': { label: '100K / mo', icon: TrendingUp,  infraWeight: 2 },
  '1m':   { label: '1M+ / mo',  icon: Users,       infraWeight: 3 },
};

const TIMELINE = {
  asap:     { label: 'ASAP',        weeks: 2,  icon: Timer },
  '30days': { label: '30 Days',     weeks: 4,  icon: CalendarRange },
  '60days': { label: '60 Days',     weeks: 8,  icon: CalendarRange },
  '90days': { label: '90+ Days',    weeks: 13, icon: CalendarRange },
  flexible: { label: 'Flexible',    weeks: 8,  icon: Clock },
};

const SUPPORT = {
  none:      { label: 'No Support',     add: 0,     icon: ShieldOff },
  basic:     { label: 'Basic',          add: 5000,  icon: ShieldHalf },
  priority:  { label: 'Priority',       add: 15000, icon: ShieldCheck },
  dedicated: { label: 'Dedicated Team', add: 35000, icon: Crown },
};

// Tech stack recommendation, keyed by service — first match by feature wins extras.
const STACK_BY_SERVICE = {
  website: ['Next.js', 'React', 'Cloudflare'],
  ecommerce: ['Shopify', 'Next.js', 'Cloudflare'],
  app: ['React Native', 'Firebase', 'Node.js'],
  saas: ['React', 'Node.js', 'AWS'],
  crm: ['React', 'Laravel', 'AWS'],
  ai: ['Next.js', 'Node.js', 'AWS'],
  marketplace: ['Next.js', 'Node.js', 'AWS'],
  enterprise: ['React', 'Node.js', 'AWS'],
};

const STEPS = [
  { key: 'service', title: 'What would you like to build?', type: 'single',
    options: Object.entries(SERVICE_BASE).map(([value, v]) => ({ value, label: v.label, icon: v.icon })) },
  { key: 'complexity', title: 'How complex is your project?', type: 'single',
    options: Object.entries(COMPLEXITY).map(([value, v]) => ({ value, label: v.label, icon: v.icon })) },
  { key: 'pages', title: 'How many pages / screens?', type: 'single',
    options: Object.entries(PAGES).map(([value, v]) => ({ value, label: v.label, icon: v.icon })) },
  { key: 'features', title: 'Which features do you need?', type: 'multi',
    options: Object.entries(FEATURES).map(([value, v]) => ({ value, label: v.label, icon: v.icon })) },
  { key: 'design', title: 'What design quality are you after?', type: 'single',
    options: Object.entries(DESIGN).map(([value, v]) => ({ value, label: v.label, icon: v.icon })) },
  { key: 'traffic', title: 'Expected monthly visitors?', type: 'single',
    options: Object.entries(TRAFFIC).map(([value, v]) => ({ value, label: v.label, icon: v.icon })) },
  { key: 'timeline', title: 'Expected launch timeline?', type: 'single',
    options: Object.entries(TIMELINE).map(([value, v]) => ({ value, label: v.label, icon: v.icon })) },
  { key: 'support', title: 'Post-launch support?', type: 'single',
    options: Object.entries(SUPPORT).map(([value, v]) => ({ value, label: v.label, icon: v.icon })) },
];

const LOADING_STAGES = [
  'Analyzing requirements…',
  'Calculating resources…',
  'Generating roadmap…',
  'Preparing proposal…',
];

/* ────────────────────────────────────────────────────────────────────────
   ESTIMATION ENGINE
   ──────────────────────────────────────────────────────────────────────── */

function formatINR(n) {
  if (n >= 100000) {
    const l = n / 100000;
    return `₹${l % 1 === 0 ? l.toFixed(0) : l.toFixed(1)}L`;
  }
  if (n >= 1000) return `₹${Math.round(n / 1000)}k`;
  return `₹${Math.round(n)}`;
}

function computeEstimate(answers) {
  const service = SERVICE_BASE[answers.service];
  if (!service) return null;

  const complexity = COMPLEXITY[answers.complexity];
  const pages = PAGES[answers.pages];
  const design = DESIGN[answers.design];
  const support = SUPPORT[answers.support];
  const traffic = TRAFFIC[answers.traffic];
  const timeline = TIMELINE[answers.timeline];
  const features = answers.features || [];

  const featureCost = features.reduce((sum, f) => sum + (FEATURES[f]?.cost || 0), 0);
  const complexityMult = complexity?.mult ?? 1;
  const designMult = design?.mult ?? 1;

  const base = service.range.map((v) =>
    Math.round((v * complexityMult + (pages?.add || 0) + featureCost) * designMult + (support?.add || 0))
  );

  // Team size
  const backendFeatureCount = features.filter((f) => FEATURES[f]?.backend).length;
  const team = ['Designer', 'Frontend Developer'];
  if (backendFeatureCount > 0) team.push('Backend Developer');
  if (complexity && (answers.complexity === 'advanced' || answers.complexity === 'enterprise' || features.length >= 4)) {
    team.push('QA Engineer');
  }
  if (answers.complexity === 'enterprise' || base[1] >= 150000) team.push('Project Manager');

  // Tech stack
  const stackSet = new Set(STACK_BY_SERVICE[answers.service] || ['React', 'Node.js']);
  if (features.includes('ai')) stackSet.add('AWS');
  if (features.includes('payment')) stackSet.add('Cloudflare');
  if (features.includes('cms') && answers.service === 'website') stackSet.add('WordPress');
  if ((traffic?.infraWeight ?? 0) >= 2) stackSet.add('AWS');
  const stack = Array.from(stackSet).slice(0, 5);

  // Realistic timeline in weeks, derived from complexity + pages + features
  const realisticWeeks = Math.max(
    2,
    Math.round(
      2 +
        (complexity ? complexity.mult * 2 : 0) +
        (pages ? pages.add / 10000 : 0) +
        features.length * 0.6
    )
  );

  // Scores (0-100)
  const complexityScore = complexity?.score ?? 30;
  const scalabilityScore = Math.min(100, 40 + (traffic?.infraWeight ?? 0) * 15 + stack.length * 5);
  const launchReadiness = timeline
    ? Math.max(20, Math.min(100, 100 - (realisticWeeks - timeline.weeks) * 10))
    : 50;
  const budgetMatch = Math.min(100, 55 + designMult * 20 + (support?.add ? 10 : 0));
  const overall = Math.round((complexityScore + scalabilityScore + launchReadiness + budgetMatch) / 4);

  // Cost breakdown (illustrative split of the upper estimate)
  const upper = base[1];
  const breakdown = [
    { label: 'Design', pct: 0.2 },
    { label: 'Development', pct: 0.4 },
    { label: 'Backend', pct: backendFeatureCount > 0 ? 0.2 : 0.08 },
    { label: 'Testing', pct: 0.08 },
    { label: 'Deployment', pct: 0.06 },
    { label: 'Support', pct: support?.add ? 0.12 : 0.04 },
  ];
  const breakdownTotal = breakdown.reduce((s, b) => s + b.pct, 0);
  const costBreakdown = breakdown.map((b) => ({
    label: b.label,
    amount: Math.round((b.pct / breakdownTotal) * upper),
  }));

  return {
    budgetLow: base[0],
    budgetHigh: base[1],
    budgetLabel: `${formatINR(base[0])} – ${formatINR(base[1])}`,
    team,
    stack,
    realisticWeeks,
    timelineLabel: timeline ? timeline.label : '—',
    scores: {
      complexity: complexityScore,
      scalability: scalabilityScore,
      launchReadiness,
      budgetMatch,
      overall,
    },
    costBreakdown,
  };
}

/* ────────────────────────────────────────────────────────────────────────
   PRESENTATIONAL PIECES
   ──────────────────────────────────────────────────────────────────────── */

function TypingTitle({ text }) {
  const [shown, setShown] = useState('');
  useEffect(() => {
    setShown('');
    let i = 0;
    const t = setInterval(() => {
      i += 1;
      setShown(text.slice(0, i));
      if (i >= text.length) clearInterval(t);
    }, 14);
    return () => clearInterval(t);
  }, [text]);
  return (
    <span>
      {shown}
      {shown.length < text.length && <span className="animate-pulse text-white/40">|</span>}
    </span>
  );
}

function ConfidenceRing({ value }) {
  const r = 26;
  const c = 2 * Math.PI * r;
  const offset = c - (value / 100) * c;
  return (
    <div className="relative w-[68px] h-[68px] flex-shrink-0">
      <svg width="68" height="68" viewBox="0 0 68 68" className="-rotate-90">
        <circle cx="34" cy="34" r={r} stroke="rgba(255,255,255,0.08)" strokeWidth="5" fill="none" />
        <motion.circle
          cx="34" cy="34" r={r} stroke="white" strokeWidth="5" fill="none" strokeLinecap="round"
          strokeDasharray={c}
          initial={false}
          animate={{ strokeDashoffset: offset }}
          transition={{ duration: 0.8, ease: 'easeOut' }}
        />
      </svg>
      <div className="absolute inset-0 flex items-center justify-center">
        <span className="text-white text-xs font-semibold">{value}%</span>
      </div>
    </div>
  );
}

function ScoreBar({ label, value }) {
  return (
    <div>
      <div className="flex items-center justify-between mb-1.5">
        <span className="text-white/40 text-[11px]">{label}</span>
        <span className="text-white/70 text-[11px] font-medium">{value}</span>
      </div>
      <div className="h-1 rounded-full bg-white/[0.06] overflow-hidden">
        <motion.div
          className="h-full rounded-full bg-gradient-to-r from-white/40 to-white"
          initial={{ width: 0 }}
          animate={{ width: `${value}%` }}
          transition={{ duration: 0.7, ease: 'easeOut' }}
        />
      </div>
    </div>
  );
}

function OptionCard({ icon: Icon, label, selected, onClick }) {
  return (
    <motion.button
      type="button"
      onClick={onClick}
      whileHover={{ y: -2 }}
      whileTap={{ scale: 0.98 }}
      className={`relative group text-left rounded-xl px-4 py-3.5 border transition-colors duration-200 flex items-center gap-3 overflow-hidden
        ${selected
          ? 'border-white/80 bg-white/[0.08]'
          : 'border-white/10 bg-white/[0.02] hover:border-white/30 hover:bg-white/[0.05]'}`}
    >
      {selected && (
        <motion.div
          layoutId="option-glow"
          className="absolute inset-0 bg-gradient-to-br from-white/10 to-transparent"
          transition={{ duration: 0.3 }}
        />
      )}
      {Icon && <Icon size={16} className={`relative flex-shrink-0 ${selected ? 'text-white' : 'text-white/40 group-hover:text-white/70'}`} />}
      <span className={`relative text-[13px] font-medium ${selected ? 'text-white' : 'text-white/60 group-hover:text-white/90'}`}>
        {label}
      </span>
      {selected && (
        <motion.span initial={{ scale: 0 }} animate={{ scale: 1 }} className="relative ml-auto flex-shrink-0">
          <Check size={14} className="text-white" />
        </motion.span>
      )}
    </motion.button>
  );
}

function LoadingOverlay({ onDone }) {
  const [stage, setStage] = useState(0);
  useEffect(() => {
    if (stage >= LOADING_STAGES.length - 1) {
      const t = setTimeout(onDone, 700);
      return () => clearTimeout(t);
    }
    const t = setTimeout(() => setStage((s) => s + 1), 550);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stage]);

  return (
    <motion.div
      initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
      className="absolute inset-0 z-20 bg-[#0a0a0a]/95 backdrop-blur-sm flex flex-col items-center justify-center gap-6 rounded-2xl"
    >
      <div className="relative w-14 h-14">
        <div className="absolute inset-0 rounded-full border border-white/10" />
        <Loader2 size={56} className="animate-spin text-white/70" strokeWidth={1.2} />
      </div>
      <div className="flex flex-col items-center gap-2 h-6">
        <AnimatePresence mode="wait">
          <motion.p
            key={stage}
            initial={{ opacity: 0, y: 6 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -6 }}
            transition={{ duration: 0.25 }}
            className="text-white/60 text-xs tracking-wide"
          >
            {LOADING_STAGES[stage]}
          </motion.p>
        </AnimatePresence>
      </div>
    </motion.div>
  );
}

/* ────────────────────────────────────────────────────────────────────────
   MAIN COMPONENT
   ──────────────────────────────────────────────────────────────────────── */

export default function AIProjectConsultant() {
  const navigate = useNavigate();
  const [step, setStep] = useState(0);
  const [answers, setAnswers] = useState({});
  const [direction, setDirection] = useState(1);
  const [phase, setPhase] = useState('questions'); // questions | result | loading
  const trackRef = useRef(null);

  const estimate = useMemo(() => computeEstimate(answers), [answers]);
  const answeredCount = STEPS.filter((s) => {
    const v = answers[s.key];
    return s.type === 'multi' ? v && v.length > 0 : !!v;
  }).length;
  const confidence = Math.round((answeredCount / STEPS.length) * 100);

  const current = STEPS[step];

  const selectSingle = (key, value) => {
    setAnswers((a) => ({ ...a, [key]: value }));
    setTimeout(() => goNext(), 260);
  };

  const toggleMulti = (key, value) => {
    setAnswers((a) => {
      const list = a[key] || [];
      const next = list.includes(value) ? list.filter((v) => v !== value) : [...list, value];
      return { ...a, [key]: next };
    });
  };

  const goNext = () => {
    setDirection(1);
    if (step < STEPS.length - 1) setStep((s) => s + 1);
    else setPhase('result');
  };

  const goBack = () => {
    setDirection(-1);
    if (phase === 'result') { setPhase('questions'); return; }
    if (step > 0) setStep((s) => s - 1);
  };

  const reset = () => {
    setStep(0);
    setAnswers({});
    setPhase('questions');
    setDirection(1);
  };

  const handleCTA = () => setPhase('loading');

  const finishToContact = () => {
    const params = new URLSearchParams({
      service: answers.service || '',
      complexity: answers.complexity || '',
      pages: answers.pages || '',
      features: (answers.features || []).join(','),
      design: answers.design || '',
      traffic: answers.traffic || '',
      timeline: answers.timeline || '',
      support: answers.support || '',
      budget: estimate?.budgetLabel || '',
    });
    navigate(`/contact?${params.toString()}`);
  };

  const canContinue = current
    ? current.type === 'multi'
      ? true // features are optional — allow skipping
      : !!answers[current.key]
    : true;

  return (
    <div className="relative bg-[#0a0a0a] border border-white/10 rounded-2xl overflow-hidden">
      {/* floating ambient blobs */}
      <div className="pointer-events-none absolute -top-24 -right-24 w-72 h-72 rounded-full bg-white/[0.03] blur-3xl" />
      <div className="pointer-events-none absolute -bottom-28 -left-20 w-72 h-72 rounded-full bg-white/[0.02] blur-3xl" />

      {/* header */}
      <div className="relative px-6 py-4 border-b border-white/10 flex items-center justify-between">
        <div className="flex items-center gap-2.5">
          <div className="w-7 h-7 rounded-lg bg-white flex items-center justify-center flex-shrink-0">
            <Sparkle size={13} className="text-black" strokeWidth={2.2} />
          </div>
          <div>
            <p className="text-white text-xs font-medium">AI Project Consultant</p>
            <p className="text-white/30 text-xs">Real-time estimate</p>
          </div>
        </div>
        <div className="flex items-center gap-1.5">
          <div className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse" />
          <span className="text-white/30 text-xs">Live</span>
        </div>
      </div>

      {/* progress */}
      {phase === 'questions' && (
        <div className="relative h-0.5 bg-white/5">
          <motion.div
            className="h-full bg-white"
            animate={{ width: `${(step / STEPS.length) * 100}%` }}
            transition={{ duration: 0.4, ease: 'easeOut' }}
          />
        </div>
      )}

      <div className="relative grid md:grid-cols-5 min-h-[460px]">
        {/* ── LEFT: conversation ── */}
        <div className="md:col-span-3 p-6 flex flex-col border-b md:border-b-0 md:border-r border-white/10 relative">
          <AnimatePresence>{phase === 'loading' && <LoadingOverlay onDone={finishToContact} />}</AnimatePresence>

          <AnimatePresence mode="wait" custom={direction}>
            {phase === 'questions' && (
              <motion.div
                key={step}
                custom={direction}
                initial={{ opacity: 0, x: direction * 24 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -direction * 24 }}
                transition={{ duration: 0.3, ease: 'easeOut' }}
                className="flex flex-col flex-1"
              >
                <p className="text-white/30 text-[11px] mb-2">Step {step + 1} of {STEPS.length}</p>
                <h3 className="text-white text-lg font-medium mb-5 min-h-[28px]" style={{ fontFamily: 'Anton, sans-serif', fontWeight: 500, letterSpacing: '0.01em' }}>
                  <TypingTitle text={current.title} />
                </h3>

                <div className={`grid gap-2.5 flex-1 content-start ${current.type === 'multi' ? 'sm:grid-cols-2' : 'sm:grid-cols-2'}`}>
                  {current.options.map((opt) => {
                    const isMulti = current.type === 'multi';
                    const selected = isMulti
                      ? (answers[current.key] || []).includes(opt.value)
                      : answers[current.key] === opt.value;
                    return (
                      <OptionCard
                        key={opt.value}
                        icon={opt.icon}
                        label={opt.label}
                        selected={selected}
                        onClick={() => (isMulti ? toggleMulti(current.key, opt.value) : selectSingle(current.key, opt.value))}
                      />
                    );
                  })}
                </div>

                <div className="flex items-center justify-between mt-6 pt-5 border-t border-white/10">
                  <button
                    onClick={goBack}
                    disabled={step === 0}
                    className="flex items-center gap-1.5 text-xs text-white/30 hover:text-white disabled:opacity-0 transition-colors"
                  >
                    <ArrowLeft size={14} /> Back
                  </button>
                  {current.type === 'multi' && (
                    <button
                      onClick={goNext}
                      className="flex items-center gap-2 bg-white text-black text-xs font-medium px-5 py-2.5 rounded-full hover:bg-white/90 transition-colors"
                    >
                      Continue <ArrowRight size={14} />
                    </button>
                  )}
                </div>
              </motion.div>
            )}

            {phase === 'result' && estimate && (
              <motion.div
                key="result"
                initial={{ opacity: 0, y: 12 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.35 }}
                className="flex flex-col flex-1"
              >
                <p className="text-white/30 text-[11px] mb-2">Project Summary</p>
                <h3 className="text-white text-lg font-medium mb-5" style={{ fontFamily: 'Anton, sans-serif', fontWeight: 500 }}>
                  Here's your tailored proposal.
                </h3>

                <div className="grid grid-cols-2 gap-3 mb-4">
                  {estimate.costBreakdown.map((b) => (
                    <div key={b.label} className="rounded-xl border border-white/10 bg-white/[0.02] px-4 py-3">
                      <p className="text-white/30 text-[11px] mb-1">{b.label}</p>
                      <p className="text-white text-sm font-semibold">{formatINR(b.amount)}</p>
                    </div>
                  ))}
                </div>

                <div className="rounded-xl border border-white/10 bg-white/[0.02] p-4 mb-4">
                  <p className="text-white/30 text-[11px] mb-2">Estimated Team</p>
                  <div className="flex flex-wrap gap-2">
                    {estimate.team.map((t) => (
                      <span key={t} className="text-[11px] bg-white/5 text-white/60 border border-white/10 px-2.5 py-1 rounded-full">{t}</span>
                    ))}
                  </div>
                </div>

                <div className="flex items-center gap-2 mt-auto pt-4 border-t border-white/10">
                  <button onClick={goBack} className="flex items-center gap-1.5 text-xs text-white/30 hover:text-white transition-colors">
                    <ArrowLeft size={14} /> Edit answers
                  </button>
                  <button
                    onClick={handleCTA}
                    className="ml-auto flex items-center gap-2 bg-white text-black text-xs font-medium px-5 py-2.5 rounded-full hover:bg-white/90 transition-colors"
                  >
                    Get Exact Quote <ArrowRight size={14} />
                  </button>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>

        {/* ── RIGHT: sticky live summary ── */}
        <div className="md:col-span-2 p-6 bg-white/[0.015] flex flex-col gap-5">
          <div className="flex items-center justify-between">
            <p className="text-white/40 text-[11px] uppercase tracking-wider">Live Summary</p>
            <ConfidenceRing value={confidence} />
          </div>

          <div className="rounded-xl border border-white/10 bg-gradient-to-b from-white/[0.04] to-transparent p-4">
            <p className="text-white/30 text-[11px] mb-1">Estimated Budget</p>
            <AnimatePresence mode="wait">
              <motion.p
                key={estimate?.budgetLabel || 'pending'}
                initial={{ opacity: 0, y: 4 }}
                animate={{ opacity: 1, y: 0 }}
                className="text-white text-2xl font-semibold"
                style={{ fontFamily: 'Anton, sans-serif', fontWeight: 500 }}
              >
                {estimate ? estimate.budgetLabel : '—'}
              </motion.p>
            </AnimatePresence>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="rounded-xl border border-white/10 bg-white/[0.02] p-3.5">
              <p className="text-white/30 text-[11px] mb-1">Timeline</p>
              <p className="text-white text-sm font-medium">{estimate ? `${estimate.realisticWeeks} wks` : '—'}</p>
            </div>
            <div className="rounded-xl border border-white/10 bg-white/[0.02] p-3.5">
              <p className="text-white/30 text-[11px] mb-1">Complexity</p>
              <p className="text-white text-sm font-medium">{COMPLEXITY[answers.complexity]?.label || '—'}</p>
            </div>
            <div className="rounded-xl border border-white/10 bg-white/[0.02] p-3.5 col-span-2">
              <p className="text-white/30 text-[11px] mb-1.5">Recommended Stack</p>
              <div className="flex flex-wrap gap-1.5">
                {estimate && estimate.stack.length > 0
                  ? estimate.stack.map((s) => (
                      <span key={s} className="text-[10px] bg-white/5 text-white/60 border border-white/10 px-2 py-0.5 rounded-full">{s}</span>
                    ))
                  : <span className="text-white/20 text-xs">—</span>}
              </div>
            </div>
            <div className="rounded-xl border border-white/10 bg-white/[0.02] p-3.5 col-span-2">
              <p className="text-white/30 text-[11px] mb-1">Team Size</p>
              <p className="text-white text-sm font-medium">{estimate ? `${estimate.team.length} specialists` : '—'}</p>
            </div>
          </div>

          <div className="rounded-xl border border-white/10 bg-white/[0.02] p-4 flex-1">
            <div className="flex items-center justify-between mb-3">
              <p className="text-white/30 text-[11px] uppercase tracking-wider">Project Score</p>
              <Gauge size={14} className="text-white/30" />
            </div>
            <div className="flex flex-col gap-3">
              <ScoreBar label="Complexity" value={estimate ? estimate.scores.complexity : 0} />
              <ScoreBar label="Scalability" value={estimate ? estimate.scores.scalability : 0} />
              <ScoreBar label="Launch Readiness" value={estimate ? estimate.scores.launchReadiness : 0} />
              <ScoreBar label="Budget Match" value={estimate ? estimate.scores.budgetMatch : 0} />
            </div>
            <div className="flex items-center justify-between mt-4 pt-3 border-t border-white/10">
              <span className="text-white/50 text-xs">Overall Score</span>
              <span className="text-white text-sm font-semibold">{estimate ? estimate.scores.overall : 0}/100</span>
            </div>
          </div>

          {phase !== 'questions' && (
            <button onClick={reset} className="text-white/30 text-xs hover:text-white transition-colors self-start">
              Start over
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
